import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtEUR, withVAT, finalPrice } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ChevronLeft, Minus, Plus, Truck, ShieldCheck, Package, Tag } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [qty, setQty] = useState(1);
    const { add } = useCart();

    useEffect(() => {
        api.get(`/products/${id}`).then((r) => {
            setP(r.data);
            // Internal tracking
            if (r.data?.id) {
                api.post("/track/product-view", {
                    product_id: r.data.id,
                    sku: r.data.sku || "",
                    name: r.data.name || "",
                }).catch(() => {});
            }
        }).catch(() => setP(null));
    }, [id]);

    if (p === null) {
        return (
            <div className="container-edge py-24 text-center font-mono">A carregar produto…</div>
        );
    }

    const inStock = p.stock > 0;
    const vat = Number(p.vat_rate ?? 23);
    const base = Number(p.price ?? 0);
    const finalP = finalPrice(p);
    const hasCampaign = p.campaign_id && finalP < base;
    const discount = hasCampaign && base > 0 ? Math.round(((base - finalP) / base) * 100) : null;

    return (
        <div data-testid="product-detail-page">
            <div className="container-edge py-6">
                <Link to="/loja" className="btn-ghost text-xs">
                    <ChevronLeft size={14} /> Voltar ao catálogo
                </Link>
            </div>
            <section className="container-edge pb-16 grid lg:grid-cols-2 gap-10">
                <div className="border-2 border-ink bg-muted aspect-square overflow-hidden">
                    {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center font-mono text-ink/40">
                            sem imagem
                        </div>
                    )}
                </div>

                <div>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                        {p.brand && <span className="badge-edge bg-paper border-ink">{p.brand}</span>}
                        {p.is_original && (
                            <span className="badge-edge bg-ink text-white border-ink">Original</span>
                        )}
                        {p.is_refurbished && (
                            <span className="badge-edge bg-accent-green text-white border-accent-green">
                                Recondicionado
                            </span>
                        )}
                        {discount && (
                            <span className="badge-edge bg-magenta text-white border-magenta">
                                {p.campaign_name ? `${p.campaign_name} · -${discount}%` : `Promoção -${discount}%`}
                            </span>
                        )}
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
                        {p.name}
                    </h1>
                    <div className="font-mono text-sm text-ink/60 mt-2" data-testid="product-sku">
                        SKU: {p.sku}
                    </div>

                    <div className="my-6 flex items-end gap-4 flex-wrap">
                        <div>
                            {hasCampaign && (
                                <div className="font-mono text-base text-ink/40 line-through">
                                    {fmtEUR(base)} s/IVA
                                </div>
                            )}
                            <div className="font-display font-black text-5xl tracking-tight text-magenta">
                                {fmtEUR(finalP)}
                                <span className="ml-2 text-base font-mono align-top text-ink/60">s/IVA</span>
                            </div>
                            <div className="font-mono text-sm text-ink/70 mt-1">
                                {fmtEUR(withVAT(finalP, vat))} c/IVA à taxa em vigor de {vat}%
                            </div>
                        </div>
                    </div>

                    <div
                        className={`inline-flex items-center gap-2 label-mono ${
                            inStock ? "text-accent-green" : "text-destructive"
                        }`}
                    >
                        <Package size={14} />
                        {inStock ? `${p.stock} em stock · Envio Imediato` : "Esgotado"}
                    </div>

                    {p.description && (
                        <p className="mt-6 text-ink/80 leading-relaxed whitespace-pre-line">
                            {p.description}
                        </p>
                    )}

                    {/* compatibility — printers extracted from supplier HTML */}
                    {p.compatible_printers && p.compatible_printers.length > 0 ? (
                        <div className="mt-6 border-2 border-ink p-4 bg-white">
                            <div className="label-mono mb-3 inline-flex items-center gap-1">
                                <Tag size={12} /> Compatível com {p.compatible_printers.length} modelo{p.compatible_printers.length > 1 ? "s" : ""} de impressora
                            </div>
                            <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                                {p.compatible_printers.map((c) => (
                                    <Link
                                        key={c}
                                        to={`/loja?printer=${encodeURIComponent(c)}`}
                                        className="text-xs font-mono border border-ink/40 px-2 py-1 hover:bg-magenta hover:text-white hover:border-magenta transition-colors"
                                        data-testid={`printer-chip-${c}`}
                                    >
                                        {c}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ) : p.compatibility && p.compatibility.length > 0 && (
                        <div className="mt-6 border-2 border-ink p-4 bg-white">
                            <div className="label-mono mb-2 inline-flex items-center gap-1">
                                <Tag size={12} /> Compatibilidades
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {p.compatibility.map((c) => (
                                    <span key={c} className="text-xs font-mono border border-ink/40 px-2 py-1">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* qty + cta */}
                    <div className="mt-8 flex items-stretch gap-3">
                        <div className="flex items-center border-2 border-ink">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="px-3 py-3 hover:bg-ink hover:text-white"
                                data-testid="qty-minus"
                            >
                                <Minus size={16} />
                            </button>
                            <input
                                value={qty}
                                onChange={(e) =>
                                    setQty(Math.max(1, Math.min(p.stock, parseInt(e.target.value || "1", 10))))
                                }
                                className="w-14 text-center font-mono py-3 border-x-2 border-ink focus:outline-none"
                                data-testid="qty-input"
                            />
                            <button
                                onClick={() => setQty((q) => Math.min(p.stock, q + 1))}
                                className="px-3 py-3 hover:bg-ink hover:text-white"
                                data-testid="qty-plus"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                        <button
                            disabled={!inStock}
                            onClick={() => {
                                add(p, qty);
                                toast.success("Adicionado ao carrinho", { description: p.name });
                            }}
                            className="btn-primary flex-1 disabled:bg-muted disabled:text-ink/40 disabled:border-ink/30 disabled:cursor-not-allowed"
                            data-testid="add-to-cart-detail-btn"
                        >
                            {inStock ? "Adicionar ao Carrinho" : "Indisponível"}
                        </button>
                    </div>

                    <div className="mt-8 grid grid-cols-2 gap-3">
                        <div className="border-2 border-ink p-4 bg-white">
                            <Truck className="text-magenta" size={20} />
                            <div className="text-sm font-bold uppercase mt-2">Envio rápido</div>
                            <div className="text-xs font-mono text-ink/60">CTT · DPD · 1-3 dias</div>
                        </div>
                        <div className="border-2 border-ink p-4 bg-white">
                            <ShieldCheck className="text-magenta" size={20} />
                            <div className="text-sm font-bold uppercase mt-2">Garantia</div>
                            <div className="text-xs font-mono text-ink/60">Até 24 meses</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
